import { useState, useEffect } from "react";
import { useToast } from "@/contexts/ToastContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  History as HistoryIcon,
  Search,
  BookOpen,
  User,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  XCircle,
} from "lucide-react";
import { circulationApi, type CheckoutRecord, type PaginatedResponse } from "@/lib/api/circulation";

const statusConfig: Record<string, { label: string; cls: string; icon: React.ElementType }> = {
  ACTIVE: { label: "Active", cls: "bg-blue-100 text-blue-700", icon: BookOpen },
  RETURNED: { label: "Returned", cls: "bg-green-100 text-green-700", icon: CheckCircle2 },
  OVERDUE: { label: "Overdue", cls: "bg-red-100 text-red-600", icon: AlertTriangle },
  LOST: { label: "Lost", cls: "bg-gray-100 text-gray-600", icon: XCircle },
};

type StatusFilter = "all" | "ACTIVE" | "RETURNED" | "OVERDUE" | "LOST";

const HistoryPage = () => {
  const toast = useToast();
  const [data, setData] = useState<PaginatedResponse<CheckoutRecord> | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadHistory();
  }, [tab, page]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const status = tab === "all" ? undefined : tab;
      const result = await circulationApi.getCheckouts({ status, page, limit: 30 });
      setData(result);
    } catch {
      toast.error("Failed to load circulation history");
    } finally {
      setLoading(false);
    }
  };

  const filteredData = data?.data?.filter((co) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (co.item?.biblio?.title ?? co.book?.title ?? "").toLowerCase().includes(q) ||
      co.user?.firstName?.toLowerCase().includes(q) ||
      co.user?.lastName?.toLowerCase().includes(q) ||
      co.user?.email?.toLowerCase().includes(q) ||
      co.item?.barcode?.toLowerCase().includes(q)
    );
  });

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Circulation History"
          description="Complete checkout and return history"
          breadcrumbItems={[
            { label: "Dashboard", href: "/dashboard" },
            { label: "Circulation", href: "/circulation" },
            { label: "History" },
          ]}
          showSearch={false}
          titleSize="md"
        />

        {/* Filter tabs + search */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex bg-muted/50 rounded-lg p-1 gap-1">
            {(["all", "ACTIVE", "RETURNED", "OVERDUE", "LOST"] as StatusFilter[]).map((t) => (
              <button
                key={t}
                onClick={() => {
                  setTab(t);
                  setPage(1);
                }}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  tab === t
                    ? "bg-background shadow font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t === "all" ? "All" : (statusConfig[t]?.label ?? t)}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by title, patron, barcode..."
              className="pl-10"
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : !filteredData || filteredData.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">
            <HistoryIcon className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>No records found</p>
          </Card>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Item</th>
                    <th className="px-4 py-3 font-medium">Patron</th>
                    <th className="px-4 py-3 font-medium">Checkout Date</th>
                    <th className="px-4 py-3 font-medium">Due Date</th>
                    <th className="px-4 py-3 font-medium">Return Date</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Renewals</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredData.map((co) => {
                    const cfg = statusConfig[co.status] ?? statusConfig.ACTIVE;
                    return (
                      <tr key={co.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <p className="font-medium truncate max-w-[200px]">
                            {co.item?.biblio?.title ?? co.book?.title ?? "Unknown"}
                          </p>
                          <p className="text-xs text-muted-foreground">{co.item?.barcode ?? ""}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="truncate max-w-[150px]">
                            {co.user?.firstName} {co.user?.lastName}
                          </p>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                          {new Date(co.checkoutDate).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {new Date(co.dueDate).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                          {co.returnDate ? new Date(co.returnDate).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="secondary" className={`${cfg.cls} text-xs`}>
                            {cfg.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-center">{co.renewalCount}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {data.page} of {data.totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= data.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </PageContainer>
    </AppLayout>
  );
};

export default HistoryPage;
